import React, { useState, useEffect } from 'react'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { BOOKMAKERS_BY_REGION, MARKET_SPORT_GROUPS, DEFAULT_FILTERS as DEFAULTS } from './propEvConfig'

const oddsStep = (val, dir) => {
  const step = val < 2 ? 0.1 : 0.5
  return Math.round((val + dir * step) * 10) / 10
}

// Allows empty string while typing; commits number on blur
const NumericInput = ({ value, onChange, defaultValue = 0, ...props }) => {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { setDraft(String(value)) }, [value])
  return (
    <input
      {...props}
      type="number"
      className="bc-range-input"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const n = parseFloat(draft)
        const final = isNaN(n) ? defaultValue : n
        setDraft(String(final))
        onChange(final)
      }}
    />
  )
}

const OddsInput = ({ value, onChange, ...props }) => {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { setDraft(String(value)) }, [value])
  return (
    <input
      {...props}
      type="number"
      className="bc-range-input"
      value={draft}
      step="any"
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const n = parseFloat(draft)
        const final = isNaN(n) ? value : n
        setDraft(String(final))
        onChange(final)
      }}
      onKeyDown={e => {
        if (e.key === 'ArrowUp')   { e.preventDefault(); const v = oddsStep(parseFloat(draft) || value,  1); setDraft(String(v)); onChange(v) }
        if (e.key === 'ArrowDown') { e.preventDefault(); const v = oddsStep(parseFloat(draft) || value, -1); setDraft(String(v)); onChange(v) }
      }}
    />
  )
}

function CollapsibleSection({ label, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bc-filter-section">
      <button className="bc-collapsible-header" onClick={() => setOpen(o => !o)}>
        <span className="bc-filter-label" style={{ margin: 0 }}>{label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {open && <div className="bc-collapsible-body">{children}</div>}
    </div>
  )
}

const REGION_LABELS = { AU: 'Australia', US: 'United States', EU: 'Europe', EXCHANGE: 'Exchanges' }

function SportGroup({ group, filters, setFilters, toggle, isExcluded }) {
  const [open, setOpen] = useState(false)
  const allOn  = group.sports.every(s => !isExcluded(filters.excludedSports, s))
  const allOff = group.sports.every(s =>  isExcluded(filters.excludedSports, s))

  const toggleGroup = (e) => {
    e.stopPropagation()
    setFilters(prev => {
      const next = new Set(prev.excludedSports)
      allOn ? group.sports.forEach(s => next.add(s)) : group.sports.forEach(s => next.delete(s))
      return { ...prev, excludedSports: next }
    })
  }

  return (
    <div className={`bc-sgroup${open ? ' open' : ''}`}>
      <div className="bc-sgroup-header" onClick={() => setOpen(o => !o)}>
        <div className="bc-sgroup-left">
          <input
            type="checkbox"
            checked={allOn}
            ref={el => { if (el) el.indeterminate = !allOn && !allOff }}
            onChange={toggleGroup}
            onClick={e => e.stopPropagation()}
          />
          <span className="bc-sgroup-name">{group.label}</span>
        </div>
        <div className="bc-sgroup-right">
          <svg className="bc-sgroup-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
          </svg>
        </div>
      </div>
      {open && (
        <div className="bc-sgroup-items">
          {group.sports.map(s => (
            <label key={s} className="bc-check-item">
              <input type="checkbox" checked={!isExcluded(filters.excludedSports, s)} onChange={() => toggle('excludedSports', s)} />
              {SPORT_LABELS[s] || s}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

const SPORT_LABELS = {
  AFL: 'AFL', AFLW: 'AFLW', NRL: 'NRL', NRLW: 'NRLW', RLWC: 'RL World Cup', MLS: 'MLS', NBA: 'NBA', WNBA: 'WNBA', NBL: 'NBL',
  MLB: 'MLB', NFL: 'NFL', NHL: 'NHL',
  EPL: 'EPL', UCL: 'Champions League', BUNDESLIGA: 'Bundesliga',
  LA_LIGA: 'La Liga', SERIE_A: 'Serie A', LIGUE_1: 'Ligue 1', A_LEAGUE: 'A-League',
  WORLD_CUP: 'World Cup',
}

// One collapsible group per sport, built from propEvConfig so the checkboxes
// and the page's filtering can never disagree on ids.
const MARKET_GROUPS = MARKET_SPORT_GROUPS.map(sg => ({
  label: sg.label,
  markets: sg.markets.map(g => g.id),
  marketLabels: Object.fromEntries(sg.markets.map(g => [g.id, g.label])),
}))

function MarketGroup({ group, filters, setFilters, toggle, isExcluded }) {
  const [open, setOpen] = useState(false)
  const allOn  = group.markets.every(m => !isExcluded(filters.excludedMarketTypes, m))
  const allOff = group.markets.every(m =>  isExcluded(filters.excludedMarketTypes, m))

  const toggleGroup = (e) => {
    e.stopPropagation()
    setFilters(prev => {
      const next = new Set(prev.excludedMarketTypes)
      allOn ? group.markets.forEach(m => next.add(m)) : group.markets.forEach(m => next.delete(m))
      return { ...prev, excludedMarketTypes: next }
    })
  }

  return (
    <div className={`bc-sgroup${open ? ' open' : ''}`}>
      <div className="bc-sgroup-header" onClick={() => setOpen(o => !o)}>
        <div className="bc-sgroup-left">
          <input
            type="checkbox"
            checked={allOn}
            ref={el => { if (el) el.indeterminate = !allOn && !allOff }}
            onChange={toggleGroup}
            onClick={e => e.stopPropagation()}
          />
          <span className="bc-sgroup-name">{group.label}</span>
        </div>
        <svg className="bc-sgroup-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </div>
      {open && (
        <div className="bc-sgroup-items">
          {group.markets.map(m => (
            <label key={m} className="bc-check-item">
              <input type="checkbox" checked={!isExcluded(filters.excludedMarketTypes, m)} onChange={() => toggle('excludedMarketTypes', m)} />
              {group.marketLabels[m] || m}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

const SPORT_GROUPS = [
  { label: 'Australian Rules', sports: ['AFL', 'AFLW'] },
  { label: 'Rugby League',     sports: ['NRL', 'NRLW', 'RLWC'] },
  { label: 'Soccer',           sports: ['EPL', 'UCL', 'BUNDESLIGA', 'LA_LIGA', 'SERIE_A', 'LIGUE_1', 'A_LEAGUE', 'MLS', 'WORLD_CUP'] },
  { label: 'Basketball',       sports: ['NBA', 'WNBA', 'NBL'] },
  { label: 'American Football',sports: ['NFL'] },
  { label: 'Baseball',         sports: ['MLB'] },
  { label: 'Ice Hockey',       sports: ['NHL'] },
]

export default function FilterPanel({
  isOpen, onClose,
  filters, setFilters,
  kellyFraction, setKellyFraction,
  bankroll, setBankroll,
  roundStakes, setRoundStakes,
  savedFilters, activeFilterId, filterName, setFilterName,
  onSaveFilter, onLoadFilter, onDeleteFilter, onToggleDefault,
  sortBy, setSortBy, sortDir, setSortDir,
}) {
  const [activeRegion, setActiveRegion] = useState('AU')

  if (!isOpen) return null

  const isExcluded = (set, v) => set.has(v)

  const toggle = (key, v) => setFilters(prev => {
    const next = new Set(prev[key])
    next.has(v) ? next.delete(v) : next.add(v)
    return { ...prev, [key]: next }
  })

  const selectRegion = (region) => setFilters(prev => {
    const next = new Set(prev.excludedBookmakers)
    BOOKMAKERS_BY_REGION[region].forEach(b => next.delete(b))
    return { ...prev, excludedBookmakers: next }
  })

  const clearRegion = (region) => setFilters(prev => {
    const next = new Set(prev.excludedBookmakers)
    BOOKMAKERS_BY_REGION[region].forEach(b => next.add(b))
    return { ...prev, excludedBookmakers: next }
  })

  const reset = () => {
    setFilters({
      excludedBookmakers:  new Set(),
      excludedMarketTypes: new Set(),
      excludedSports:      new Set(),
      odds: DEFAULTS.odds, ev: DEFAULTS.ev, hoursUntil: DEFAULTS.hoursUntil, minBooks: 5,
    })
    setKellyFraction('1/2')
    setBankroll(1000)
    setRoundStakes('5')
  }

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="bc-filter-panel">
        <div className="bc-panel-header">
          <p className="bc-panel-title">Filters</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="bc-btn bc-reset-header-btn" onClick={reset} style={{ fontSize: '12px', padding: '5px 10px' }}>Reset to default</button>
            <button className="bc-panel-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="bc-panel-body">

          {/* Saved Filters */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Saved Filters</span>
            {savedFilters.length === 0 ? (
              <p className="bc-saved-empty">
                Set your filters below, then save them at the bottom.
              </p>
            ) : (
              <div className="bc-saved-list">
                {savedFilters.map(f => (
                  <div
                    key={f.id}
                    className={`bc-saved-item${f.isDefault ? ' is-default' : ''}${activeFilterId === f.id ? ' is-active' : ''}`}
                    onClick={() => onLoadFilter(f)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="bc-saved-load">{f.name}</span>
                    <div className="bc-saved-actions">
                      <button className={`bc-saved-star${f.isDefault ? ' active' : ''}`} onClick={e => { e.stopPropagation(); onToggleDefault(f.id) }} title={f.isDefault ? 'Remove default' : 'Set as default'}>★</button>
                      <button className="bc-saved-delete" onClick={e => { e.stopPropagation(); onDeleteFilter(f.id) }} title="Delete">×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sports */}
          <CollapsibleSection label="Sports">
            <div className="bc-sgroup-list">
              {SPORT_GROUPS.map(group => (
                <SportGroup key={group.label} group={group} filters={filters} setFilters={setFilters} toggle={toggle} isExcluded={isExcluded} />
              ))}
            </div>
          </CollapsibleSection>

          {/* Bookmakers */}
          <CollapsibleSection label="Bookmakers">
            <div className="bc-region-tabs">
              {Object.keys(BOOKMAKERS_BY_REGION).map(region => (
                <button
                  key={region}
                  className={`bc-region-tab${activeRegion === region ? ' active' : ''}`}
                  onClick={() => setActiveRegion(region)}
                >
                  {REGION_LABELS[region]}
                </button>
              ))}
            </div>
            <div className="bc-book-bulk-btns">
              <button className="bc-bulk-btn select" onClick={() => selectRegion(activeRegion)}>Select All</button>
              <button className="bc-bulk-btn clear" onClick={() => clearRegion(activeRegion)}>Clear All</button>
            </div>
            <div className="bc-book-list">
              {BOOKMAKERS_BY_REGION[activeRegion].map(b => {
                const active = !isExcluded(filters.excludedBookmakers, b)
                return (
                  <button
                    key={b}
                    className={`bc-book-row${active ? ' active' : ''}`}
                    onClick={() => toggle('excludedBookmakers', b)}
                  >
                    <div className="bc-book-row-logo-wrap">
                      {BOOKMAKER_LOGOS[b]
                        ? <img src={BOOKMAKER_LOGOS[b]} alt={b} className="bc-book-row-logo" />
                        : <span className="bc-book-row-abbr">{b.slice(0, 2)}</span>}
                    </div>
                    <span className="bc-book-row-name">{b}</span>
                  </button>
                )
              })}
            </div>
          </CollapsibleSection>

          {/* Market Types */}
          <CollapsibleSection label="Market Types">
            <div className="bc-sgroup-list">
              {MARKET_GROUPS.map(group => (
                <MarketGroup
                  key={group.label}
                  group={group}
                  filters={filters}
                  setFilters={setFilters}
                  toggle={toggle}
                  isExcluded={isExcluded}
                />
              ))}
            </div>
          </CollapsibleSection>

          {/* Diff % */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Diff %</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <NumericInput value={filters.ev[0]} defaultValue={0}
                  onChange={v => setFilters(prev => ({ ...prev, ev: [v, prev.ev[1]] }))} />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <NumericInput value={filters.ev[1]} defaultValue={100}
                  onChange={v => setFilters(prev => ({ ...prev, ev: [prev.ev[0], v] }))} />
              </div>
            </div>
          </div>

          {/* Odds */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Odds</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <OddsInput value={filters.odds[0]} onChange={v => setFilters(prev => ({ ...prev, odds: [v, prev.odds[1]] }))} />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <OddsInput value={filters.odds[1]} onChange={v => setFilters(prev => ({ ...prev, odds: [prev.odds[0], v] }))} />
              </div>
            </div>
          </div>

          {/* Starts In */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Starts In (hours)</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <NumericInput value={filters.hoursUntil[0]} defaultValue={0}
                  onChange={v => setFilters(prev => ({ ...prev, hoursUntil: [v, prev.hoursUntil[1]] }))} />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <NumericInput value={filters.hoursUntil[1]} defaultValue={120}
                  onChange={v => setFilters(prev => ({ ...prev, hoursUntil: [prev.hoursUntil[0], v] }))} />
              </div>
            </div>
          </div>

          {/* Min Books */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Min Bookmakers</span>
            <NumericInput value={filters.minBooks} defaultValue={1} min={1}
              style={{ width: '80px' }}
              onChange={v => setFilters(prev => ({ ...prev, minBooks: v }))} />
          </div>

          {/* Sort — only rendered on mobile (when props are passed) */}
          {setSortBy && (
            <div className="bc-filter-section">
              <span className="bc-filter-label">Sort By</span>
              <select
                className="bc-select"
                value={`${sortBy}:${sortDir}`}
                onChange={e => {
                  const [col, dir] = e.target.value.split(':')
                  setSortBy(col); setSortDir(dir)
                }}
              >
                <option value="ev_percentage:desc">Diff % ↓ (High to Low)</option>
                <option value="ev_percentage:asc">Diff % ↑ (Low to High)</option>
                <option value="original_odds:desc">Odds ↓ (High to Low)</option>
                <option value="original_odds:asc">Odds ↑ (Low to High)</option>
                <option value="kelly_stake:desc">Stake ↓ (High to Low)</option>
                <option value="commence_time:asc">Start Time ↑ (Soonest)</option>
                <option value="commence_time:desc">Start Time ↓ (Latest)</option>
              </select>
            </div>
          )}

          {/* Stake Settings */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Stake Settings</span>
            <div style={{ marginBottom: '10px' }}>
              <span className="bc-range-sublabel" style={{ display: 'block', marginBottom: '5px' }}>Kelly Fraction</span>
              <select className="bc-select" value={kellyFraction} onChange={e => setKellyFraction(e.target.value)}>
                <option value="1/4">1/4 Kelly (Conservative)</option>
                <option value="1/2">1/2 Kelly (Recommended)</option>
                <option value="3/4">3/4 Kelly (Aggressive)</option>
                <option value="Full">Full Kelly (Maximum)</option>
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <span className="bc-range-sublabel" style={{ display: 'block', marginBottom: '5px' }}>Bankroll ($)</span>
              <NumericInput value={bankroll} defaultValue={1000} min={1}
                onChange={v => setBankroll(v)} />
            </div>
            <div>
              <span className="bc-range-sublabel" style={{ display: 'block', marginBottom: '5px' }}>Rounding</span>
              <select className="bc-select" value={roundStakes} onChange={e => setRoundStakes(e.target.value)}>
                <option value="none">No rounding</option>
                <option value="1">Round to $1</option>
                <option value="5">Round to $5</option>
              </select>
            </div>
          </div>

        </div>

        <div className="bc-panel-footer">
          <div className="bc-save-row">
            <input
              className="bc-range-input"
              placeholder="Filter name..."
              value={filterName}
              onChange={e => setFilterName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSaveFilter(filterName)}
            />
            <button className="bc-btn" onClick={() => onSaveFilter(filterName)}>Save Current</button>
          </div>
          <div className="bc-footer-actions">
            <button className="bc-btn bc-reset-footer-btn" onClick={reset}>Reset</button>
            <button className="bc-btn bc-apply-btn" onClick={onClose}>Apply</button>
          </div>
        </div>
      </div>
    </>
  )
}
